import { Game, Shot, getZoneFromShotType } from '../types/curling';
import { targetBandScore } from './sheetScoring';

// Weight control: how often a player keeps their thrown weight inside the
// safe band relative to the called weight. The band is asymmetric because a
// slightly heavy miss (blowing through) is worse than a slightly light one —
// safe from -0.25s (heavy) to +0.5s (light) — and score falls linearly to
// zero a further 1.5s past either edge (-1.75s / +2.00s).
const WEIGHT_CONTROL_BAND = { lo: -0.25, hi: 0.5, tolerance: 1.5 };

// Curl control: same shape, reusing the safe/extreme band already shown on
// the player summary's curl-deviation histogram (green -4" to +4", worst
// color at +/-16") so this score and that chart never disagree about what
// "safe" means. shot.curl/expectedCurl are stored in feet, so the deviation
// is converted to inches before comparing against this band.
const CURL_CONTROL_BAND = { lo: -4, hi: 4, tolerance: 12 };
const FEET_TO_INCHES = 12;

function average(values: number[]): number | null {
  return values.length > 0 ? values.reduce((sum, v) => sum + v, 0) / values.length : null;
}

// Every shot's turn crossed with whether it's a draw (zones 1-10, guards
// included) or a hit (zones 11-16) — the four categories a player's weight
// and curl control get broken down into. Zone 0 (hogged) is neither and is
// left out of every bucket.
export type ShotBucket = 'cwDraw' | 'ccwDraw' | 'cwHit' | 'ccwHit';
export const SHOT_BUCKETS: ShotBucket[] = ['cwDraw', 'ccwDraw', 'cwHit', 'ccwHit'];
export const SHOT_BUCKET_LABELS: Record<ShotBucket, string> = {
  cwDraw: 'CW Draw', ccwDraw: 'CCW Draw', cwHit: 'CW Hit', ccwHit: 'CCW Hit',
};

function bucketFor(shot: Shot): ShotBucket | null {
  const zone = getZoneFromShotType(shot.shotType);
  if (zone < 1 || zone > 16) return null;
  const isHit = zone >= 11;
  const isClockwise = shot.turn === 'clockwise';
  if (isHit) return isClockwise ? 'cwHit' : 'ccwHit';
  return isClockwise ? 'cwDraw' : 'ccwDraw';
}

export interface BucketScore {
  weightControlScore: number | null;
  weightShotCount: number;
  curlControlScore: number | null;
  curlShotCount: number;
}

export interface PlayerScore {
  playerName: string;
  team: 'team1' | 'team2';
  totalShots: number;
  // Traditional 0-4 shot-call scale, kept for tradition rather than as the primary measure.
  accuracyPercent: number | null;
  weightControlScore: number | null;
  weightShotCount: number;
  curlControlScore: number | null;
  curlShotCount: number;
  // The primary score: weight control and curl control averaged and rebased to a percentage,
  // same recipe as the sheet's own overall score.
  overallScore: number | null;
  buckets: Record<ShotBucket, BucketScore>;
}

export function computePlayerScores(game: Game): PlayerScore[] {
  interface Accumulator {
    playerName: string;
    team: 'team1' | 'team2';
    totalShots: number;
    excellent: number; good: number; fair: number; poor: number; miss: number;
    weightScores: number[];
    curlScores: number[];
    bucketWeightScores: Record<ShotBucket, number[]>;
    bucketCurlScores: Record<ShotBucket, number[]>;
  }
  const emptyBuckets = (): Record<ShotBucket, number[]> => ({ cwDraw: [], ccwDraw: [], cwHit: [], ccwHit: [] });
  const byPlayer = new Map<string, Accumulator>();

  game.ends.forEach(end => {
    end.shots.forEach(shot => {
      if (shot.skipped || !shot.playerName) return;

      if (!byPlayer.has(shot.playerName)) {
        byPlayer.set(shot.playerName, {
          playerName: shot.playerName, team: shot.team, totalShots: 0,
          excellent: 0, good: 0, fair: 0, poor: 0, miss: 0,
          weightScores: [], curlScores: [],
          bucketWeightScores: emptyBuckets(), bucketCurlScores: emptyBuckets(),
        });
      }
      const acc = byPlayer.get(shot.playerName)!;
      acc.totalShots++;
      if (shot.result) acc[shot.result]++;
      const bucket = bucketFor(shot);

      if (shot.time > 0 && shot.expectedTime > 0) {
        const dev = shot.time - shot.expectedTime;
        const score = targetBandScore(dev, WEIGHT_CONTROL_BAND.lo, WEIGHT_CONTROL_BAND.hi, WEIGHT_CONTROL_BAND.tolerance);
        acc.weightScores.push(score);
        if (bucket) acc.bucketWeightScores[bucket].push(score);
      }

      if (shot.curl != null && shot.expectedCurl != null) {
        const devInches = (shot.curl - shot.expectedCurl) * FEET_TO_INCHES;
        const score = targetBandScore(devInches, CURL_CONTROL_BAND.lo, CURL_CONTROL_BAND.hi, CURL_CONTROL_BAND.tolerance);
        acc.curlScores.push(score);
        if (bucket) acc.bucketCurlScores[bucket].push(score);
      }
    });
  });

  return Array.from(byPlayer.values()).map(acc => {
    const accuracyPercent = acc.totalShots > 0
      ? ((4 * acc.excellent + 3 * acc.good + 2 * acc.fair + acc.poor) / (4 * acc.totalShots)) * 100
      : null;
    const weightControlScore = average(acc.weightScores);
    const curlControlScore = average(acc.curlScores);

    const primaryScores = [weightControlScore, curlControlScore].filter((v): v is number => v !== null);
    const overallScore = primaryScores.length > 0
      ? (primaryScores.reduce((sum, v) => sum + v, 0) / (primaryScores.length * 10)) * 100
      : null;

    const buckets = Object.fromEntries(SHOT_BUCKETS.map(bucket => [bucket, {
      weightControlScore: average(acc.bucketWeightScores[bucket]),
      weightShotCount: acc.bucketWeightScores[bucket].length,
      curlControlScore: average(acc.bucketCurlScores[bucket]),
      curlShotCount: acc.bucketCurlScores[bucket].length,
    }])) as Record<ShotBucket, BucketScore>;

    return {
      playerName: acc.playerName, team: acc.team, totalShots: acc.totalShots,
      accuracyPercent, weightControlScore, weightShotCount: acc.weightScores.length,
      curlControlScore, curlShotCount: acc.curlScores.length, overallScore, buckets,
    };
  });
}

export interface TeamScore {
  overallScore: number | null;
  weightControlScore: number | null;
  curlControlScore: number | null;
}

/** A team's score is the simple mean of its players' scores — same unweighted-average
 *  recipe the sheet score uses to roll up its own sub-scores. */
export function computeTeamScore(players: PlayerScore[]): TeamScore {
  return {
    overallScore: average(players.map(p => p.overallScore).filter((v): v is number => v !== null)),
    weightControlScore: average(players.map(p => p.weightControlScore).filter((v): v is number => v !== null)),
    curlControlScore: average(players.map(p => p.curlControlScore).filter((v): v is number => v !== null)),
  };
}
